// ButtonBadge.tsx — a one-line segmented button: a short lead segment, a
// rule, then the label. The compact list item for sidebars that would
// otherwise stack cards, and the shape of small toolbar actions.
//
//   [ 3d │ Session title that ellipsizes… ]  🗑
//
// Neither segment is filled: the outline and the rule between them are
// the whole chrome, so a list of them reads as one quiet column and a
// fill never sits behind text (C-MSG-1). Corners are the ordinary button
// radius, not a pill. The lead is a count, an age, a BadgeDot or an icon;
// the label is the item's name on one line, and anything longer — a
// description, an exact timestamp — belongs in the `title` tooltip, not
// in a second line. Row actions (delete, unpin…) sit outside the button,
// because a button nested in a button is invalid markup, and stay out of
// sight until the row is hovered or holds focus — so they take no line of
// their own and remain reachable by keyboard.
//
// Chrome lives in `common/badges.css` (the `.button-badge*` block), which
// the app entrypoint already imports for StatusBadge.
import React from 'react';
import type { StatusTone } from './StatusBadge';

export interface ButtonBadgeProps
    extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
    /** The lead segment: a short token — a count, an age, a BadgeDot, an icon. */
    badge: React.ReactNode;
    /** The selected item: full-strength outline plus an inset ring. */
    active?: boolean;
    /**
     * A per-item identity color (any CSS color) for the outline only; the
     * text stays the body color. Unset, the outline takes the body color
     * too. Never a fill behind the label (C-MSG-1).
     */
    accent?: string;
    /**
     * Always-visible controls right after the pill — state the row must
     * show at rest, such as a tree node's expand toggle and child count.
     */
    trailing?: React.ReactNode;
    /** Row actions rendered after the pill, revealed on hover/focus. */
    actions?: React.ReactNode;
}

export const ButtonBadge: React.FC<ButtonBadgeProps> = ({
    badge,
    active = false,
    accent,
    trailing,
    actions,
    className,
    style,
    children,
    ...rest
}) => {
    const classes = ['btn', 'button-badge'];
    if (active) classes.push('active');
    if (className) classes.push(className);
    // A custom property, not a color declaration: badges.css derives the
    // label, the idle outline and the selected ring from this one value.
    const accentStyle = accent
        ? ({ '--button-badge-accent': accent } as React.CSSProperties)
        : undefined;

    return (
        <span className="button-badge-row">
            <button
                type="button"
                className={classes.join(' ')}
                aria-pressed={active}
                style={{ ...accentStyle, ...style }}
                {...rest}
            >
                <span className="button-badge-lead">{badge}</span>
                <span className="button-badge-label">{children}</span>
            </button>
            {trailing && <span className="button-badge-trailing">{trailing}</span>}
            {actions && <span className="button-badge-actions">{actions}</span>}
        </span>
    );
};

export interface BadgeDotProps {
    /** Semantic tone; `success` for live / running. */
    tone?: Exclude<StatusTone, 'neutral'>;
    /** Spoken name of the state, since the dot itself says nothing. */
    label: string;
}

/** A small filled dot for a ButtonBadge lead: live state, not a count. */
export const BadgeDot: React.FC<BadgeDotProps> = ({ tone = 'success', label }) => (
    <span
        className={`button-badge-dot button-badge-dot-${tone}`}
        role="img"
        aria-label={label}
    />
);

export default ButtonBadge;
